import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { Container, Box, Tab, Tabs, InputBase, Chip, TextField, CircularProgress, Button, Alert } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Document, Page, pdfjs } from 'react-pdf';
import renderHTML from 'html-react-parser';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-markup';
import './prism.css';
import 'prismjs/components/prism-jsx';

import {
  PREVIEW_LOADING,
  PREVIEW_CANCEL,
  PREVIEW_LOADED,
  SHIPMENT_LOADED,
  OPEN_SHIPMENT_INFO_EDITOR,
  TEMPLATE_CREATED,
  LOAD_TEMPLATE_SUCCESSED,
  CANCEL_CREATE_TEMPLATE,
} from '../../reducers/TemplateReducer';
import { Context } from '../../providers/TemplateProvider';
import { previewTemplate, getShipment, getTemplate, createTemplate } from '../../repositories/HttpRepository';
import TabPanel from '../../components/TabPanel';
import ShipmentInfoEditor from './ShipmentInfoEdittor';

import { AxlButton } from '../../components/AxlButton';

const useStyles = makeStyles()((theme) => ({
  editTemplateContainer: {
    height: 'calc(100% - 60px)',
  },
  searchBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
  },
  codeBox: {
    border: '1px solid #ccc',
    height: '100%',
    overflow: 'scroll',
  },
  container: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 6fr) minmax(0, 4fr)',
    gap: '15px',
    paddingBottom: '15px',
  },
  previewBox: {
    width: '100%',
    minHeight: '578px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  previewBtn: {
    marginLeft: 'auto',
  },
  pagination: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '10px',
  },
  pdfBox: {
    height: '578px',
    width: '384px',
    border: '1px solid #ccc',
  },
  referLink: {
    color: '#ccc',
    fontSize: '0.8rem',
    float: 'right',
  },
  loading: {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    minHeight: '578px',
    height: '100%',
    width: '384px',
    position: 'absolute',
    top: '9px',
    '&>span': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
    },
    zIndex: 1000,
  },
  contentDes: {
    boxSizing: 'border-box',
  },
  customizeZpl: {
    marginTop: '15px',
    padding: '15px',
    border: '1px solid #ccc',
    backgroundColor: '#f8f8f8',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 5,
  },
}));

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

const PREVIEW_FORMAT = {
  PDF: 'PDF',
  HTML: 'HTML',
  ZPL: 'ZPL',
};

export default React.memo(function CreateTemplate() {
  const { classes } = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const { state, dispatch } = useContext(Context);
  const { shipmentId, isLoadingPreview, pdf, png, rawHtml, previewHtml, rawJson, width, height } = state;

  const [searchShipmentId, setSearchShipmentId] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [version, setVersion] = useState('');
  const [errorVersion, setErrorVersion] = useState('');
  const [desc, setDesc] = useState('');
  const [error, setError] = useState(null);
  const [html, setHtml] = useState(rawHtml);
  const [json, setJson] = useState(rawJson);
  const [previewFormat, setPreviewFormat] = useState(PREVIEW_FORMAT.PDF);
  const [labelWidth, setLabelWidth] = useState(4);
  const [labelHeight, setLabelHeight] = useState(6);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const handleChange = (event, newValue) => {
    setActiveIndex(newValue);
  };

  const handleChangeShipmentId = (event) => {
    setSearchShipmentId(event.target.value);
  };

  const handleChangeVersion = (event) => {
    setVersion(event.target.value);
  };
  const handleChangeDesc = (event) => {
    setDesc(event.target.value);
  };

  const loadShipment = (shipmentId) => {
    const id = searchShipmentId.trim() || shipmentId;
    if (!id) {
      setError('Shipment ID is required');
      return;
    }
    getShipment(id).then((resp) => {
      if (resp.ok) {
        dispatch({ type: SHIPMENT_LOADED, data: resp.data, shipmentId: id });
      } else {
        dispatch({ type: PREVIEW_CANCEL });
        if (resp.status === 404) {
          setError('Shipment ID is not found.');
        }
      }
    });
  };

  const handleServerError = (message) => {
    setError(message);
    dispatch({ type: PREVIEW_LOADED });
  };

  const preview = async (params) => {
    try {
      const payload = {
        html: editorContent,
        json: editorContent,
        shipment_id: shipmentId,
        format: previewFormat,
        width: labelWidth,
        height: labelHeight,
        page_index: pageNumber - 1,
        ...params,
      };

      const format = payload.format;
      if (!payload.shipment_id) return setError('Shipment ID is required');
      if (!payload.json && !payload.html) return setError('Missing template content');

      dispatch({ type: PREVIEW_LOADING });

      switch (format) {
        case PREVIEW_FORMAT.PDF: {
          const params = {
            uri: '/templates/download/pdf-label',
            data: payload,
            responseType: 'blob',
          };
          const { data, ok } = await previewTemplate(params);
          if (!ok) return handleServerError('Invalid data');

          dispatch({ type: PREVIEW_LOADED, pdf: data });
          break;
        }
        case PREVIEW_FORMAT.ZPL: {
          let params = { uri: '/templates/download/zpl', data: payload };
          const { data, ok } = await previewTemplate(params);
          if (!ok) return handleServerError('Invalid data');

          const breakPoints = data.match(/\^XZ/g);
          const numPages = breakPoints ? breakPoints.length : 1;
          setNumPages(numPages);

          params = {
            uri: '/templates/download/zpl-to-png',
            data: {
              zpl: data,
              width: payload.width,
              height: payload.height,
              page_index: payload.page_index,
            },
            responseType: 'blob',
          };
          const response = await previewTemplate(params);
          if (!response.ok) return handleServerError('Invalid data');

          const png = URL.createObjectURL(response.data);
          dispatch({ type: PREVIEW_LOADED, png });
          break;
        }
        case PREVIEW_FORMAT.HTML: {
          let shipment;

          if (payload.shipment_id) {
            const response = await getShipment(payload.shipment_id);
            shipment = response.data;
          }

          if (!shipment) return handleServerError('Shipment not found');

          const params = {
            uri: '/templates/preview',
            data: { ...payload, shipment_id: '', shipment_info: shipment },
          };
          const { data, ok } = await previewTemplate(params);
          if (!ok) return handleServerError('Invalid data');

          dispatch({ type: PREVIEW_LOADED, previewHtml: data.html });
          break;
        }
        default:
          dispatch({ type: PREVIEW_CANCEL });
      }

      setError('');
      setSearchShipmentId('');
    } catch (error) {
      // console.log(error);
      setError(error.toString());
      dispatch({ type: PREVIEW_CANCEL });
    }
  };

  const create = (draft) => () => {
    if (version.trim().length < 0 || !version) {
      setErrorVersion('Version is required');
      return;
    }

    createTemplate({
      version,
      desc,
      html: html,
      json: json,
      shipment_id: shipmentId,
      data: JSON.stringify(state.shipmentInfo),
      draft: !!draft,
      width: labelWidth,
      height: labelHeight,
    }).then((resp) => {
      if (resp.ok) {
        dispatch({ type: TEMPLATE_CREATED });
        setErrorVersion('');
        navigate('/templates');
      }
    });
  };

  const cancel = () => {
    dispatch({ type: CANCEL_CREATE_TEMPLATE });
    navigate('/templates');
  };

  const openShipmentInfoEditor = (e) => {
    dispatch({ type: OPEN_SHIPMENT_INFO_EDITOR });
  };

  const handleData = (data) => {
    const { json_template, html_template, width: w, height: h } = data;
    let format = PREVIEW_FORMAT.HTML;
    if (json_template) format = PREVIEW_FORMAT.PDF;

    const content = json_template || html_template || '';

    setPreviewFormat(format);
    setEditorContent(content);
    setHtml(html_template || '');
    setJson(json_template || '');
    setDesc(data.desc);
    setVersion(data.version + ' clone');
    preview({
      format,
      html: html_template,
      json: json_template,
      shipment_id: data.shipment_id,
      width: w,
      height: h,
    });
  };

  const removeShipmentId = () => {
    dispatch({ type: CANCEL_CREATE_TEMPLATE });
    setSearchShipmentId('');
  };

  const handleChangeFormat = (format) => {
    if (previewFormat === format) return;

    setError('');
    setPageNumber(1);
    setPreviewFormat(format);
  };

  const handleChangeEditorContent = (content) => {
    setEditorContent(content);
    if (previewFormat === PREVIEW_FORMAT.HTML) setHtml(content);
    if (previewFormat !== PREVIEW_FORMAT.HTML) setJson(content);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleChangePage = (nextPage) => {
    setPageNumber(nextPage);
  };

  const renderPreview = () => {
    if (!shipmentId) return;
    if ((shipmentId && ((!pdf && !png && !previewHtml) || error)) || error) return <Box>No preview</Box>;

    switch (previewFormat) {
      case PREVIEW_FORMAT.HTML: {
        return (
          previewHtml && (
            <Box style={{ border: '1px solid #ccc' }} sx={{ maxWidth: 384 }}>
              {renderHTML(previewHtml)}
            </Box>
          )
        );
      }
      case PREVIEW_FORMAT.PDF: {
        return (
          pdf && (
            <Document className={classes.pdfBox} file={pdf} onLoadSuccess={onDocumentLoadSuccess}>
              <Page width={384} pageNumber={pageNumber} />
            </Document>
          )
        );
      }
      case PREVIEW_FORMAT.ZPL: {
        return png && <img src={png} style={{ maxWidth: '384px', border: '1px solid #ccc' }} />;
      }
      default:
        return <Box>No preview</Box>;
    }
  };

  useEffect(() => {
    if (params?.version) {
      getTemplate(params.version).then((resp) => {
        if (resp.ok) {
          dispatch({ type: LOAD_TEMPLATE_SUCCESSED, data: resp.data });
          handleData(resp.data);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (width) setLabelWidth(width);
    if (height) setLabelHeight(height);
  }, [width, height]);

  useEffect(() => {
    switch (previewFormat) {
      case PREVIEW_FORMAT.PDF: {
        setActiveIndex(0);
        setEditorContent(json);
        if (!pdf) preview({ format: previewFormat, json: json });
        break;
      }
      case PREVIEW_FORMAT.ZPL: {
        setActiveIndex(1);
        setEditorContent(json);
        if (!png) preview({ format: previewFormat, json: json });
        break;
      }
      case PREVIEW_FORMAT.HTML: {
        setActiveIndex(2);
        setEditorContent(html);
        if (!previewHtml) preview({ format: previewFormat, html: html });
        break;
      }
      default:
    }
  }, [previewFormat]);

  useEffect(() => {
    if (previewFormat === PREVIEW_FORMAT.ZPL) preview();
  }, [pageNumber]);

  return (
    <Container maxWidth={false}>
      {state.shipmentInfo && <ShipmentInfoEditor />}
      <div style={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 2,
            width: '50%',
            padding: 1,
          }}
        >
          <Box className={classes.searchBox}>
            <InputBase placeholder="Enter shipment ID" type="number" value={searchShipmentId} onChange={handleChangeShipmentId} inputProps={{ 'aria-label': 'Enter shipment ID' }} />
            <AxlButton type="button" size="small" color="secondary" onClick={loadShipment}>
              <SearchIcon />
            </AxlButton>
            {state.shipmentId && state.shipmentId !== '' && <Chip label={state.shipmentId} onDelete={() => removeShipmentId()} />}
          </Box>
          <Box>
            <AxlButton onClick={openShipmentInfoEditor} variant="contained" color="primary" style={{ display: 'none' }}>
              Edit Sample Data
            </AxlButton>
          </Box>
        </Box>
        <Box className={classes.container}>
          <Box>
            <Box sx={{ display: 'flex', p: 1 }}>
              <AxlButton type="button" color={previewFormat !== PREVIEW_FORMAT.HTML ? 'primary' : 'secondary'} size="small" onClick={() => handleChangeFormat(PREVIEW_FORMAT.PDF)}>
                JSON
              </AxlButton>
              <AxlButton type="button" color={previewFormat === PREVIEW_FORMAT.HTML ? 'primary' : 'secondary'} size="small" onClick={() => handleChangeFormat(PREVIEW_FORMAT.HTML)}>
                HTML
              </AxlButton>
              <AxlButton type="button" onClick={() => preview()} variant="contained" color="secondary" className={classes.previewBtn}>
                Preview
              </AxlButton>
            </Box>
            {error && error !== '' && <Alert severity="error">{error}</Alert>}
            <Box className="container_editor_area" sx={{ marginTop: 1 }}>
              {previewFormat !== PREVIEW_FORMAT.HTML && (
                <Editor
                  value={editorContent}
                  onValueChange={(code) => handleChangeEditorContent(code)}
                  highlight={(code) => highlight(code, languages.json)}
                  padding={10}
                  className="container__editor"
                />
              )}
              {previewFormat === PREVIEW_FORMAT.HTML && (
                <Editor
                  value={editorContent}
                  onValueChange={(code) => handleChangeEditorContent(code)}
                  highlight={(code) => highlight(code, languages.html)}
                  padding={10}
                  className="container__editor"
                />
              )}
            </Box>
            <a target="_blank" className={classes.referLink} href="https://mustache.github.io/mustache.5.html" rel="noreferrer">
              syntax
            </a>
          </Box>
          <Box>
            <Box>
              <Tabs value={activeIndex} onChange={handleChange} indicatorColor="primary" textColor="primary" variant="fullWidth" aria-label="full width tabs example">
                <Tab label="PDF" {...a11yProps(0)} onClick={() => handleChangeFormat(PREVIEW_FORMAT.PDF)} />
                <Tab label="ZPL" {...a11yProps(1)} onClick={() => handleChangeFormat(PREVIEW_FORMAT.ZPL)} />
                <Tab label="HTML" {...a11yProps(2)} onClick={() => handleChangeFormat(PREVIEW_FORMAT.HTML)} />
              </Tabs>
              <TabPanel activeIndex={0} index={0} dir={theme.direction}>
                <Box className={classes.previewBox} sx={{ paddingTop: 1 }}>
                  {!shipmentId && <Box>Please select shipment ID to preview</Box>}
                  {isLoadingPreview && (
                    <div className={classes.loading}>
                      <span>
                        <CircularProgress />
                      </span>
                    </div>
                  )}
                  {renderPreview()}
                </Box>
                <div className={classes.pagination}>
                  {numPages >= 1 && (
                    <small>
                      Page {pageNumber} of {numPages}
                    </small>
                  )}
                  <Button size="small" variant="text" color="primary" disabled={pageNumber <= 1} onClick={() => handleChangePage(pageNumber - 1)}>
                    Previous
                  </Button>
                  <Button size="small" variant="text" color="primary" disabled={pageNumber >= numPages} onClick={() => handleChangePage(pageNumber + 1)}>
                    Next
                  </Button>
                </div>
              </TabPanel>
            </Box>
            {[PREVIEW_FORMAT.ZPL, PREVIEW_FORMAT.PDF].includes(previewFormat) && (
              <Box className={classes.customizeZpl}>
                <span>
                  <strong fontSize="14px">Label Size</strong> (inches):
                </span>
                <input type="number" value={labelWidth} placeholder="Width" min={0} style={{ width: '100px' }} onChange={(e) => setLabelWidth(e.target.value)} />
                <input type="number" value={labelHeight} placeholder="Height" min={0} style={{ width: '100px' }} onChange={(e) => setLabelHeight(e.target.value)} />
              </Box>
            )}
          </Box>
        </Box>
      </div>
      <Box
        className={classes.contentDes}
        sx={{
          width: '100%',
          paddingLeft: 2,
          marginBottom: 4,
        }}
      >
        <TextField fullWidth multiline variant="outlined" id="desc" label="Description" value={desc} rows={3} onChange={handleChangeDesc} />
        <TextField
          fullWidth
          required
          id="version"
          label="Version"
          error={Boolean(errorVersion)}
          helperText={errorVersion}
          value={version}
          onChange={handleChangeVersion}
          style={{ marginTop: '10px' }}
        />
        <br />
        <br />
        <AxlButton
          onClick={cancel}
          variant="contained"
          style={{
            color: 'black',
            backgroundColor: '#E0E0E0',
            textTransform: 'none',
          }}
        >
          Cancel
        </AxlButton>
        &nbsp; &nbsp;
        <AxlButton color="primary" onClick={create(true)} variant="contained">
          Save Draft
        </AxlButton>
        &nbsp; &nbsp;
        <AxlButton color="primary" onClick={create()} variant="contained">
          Save
        </AxlButton>
      </Box>
    </Container>
  );
});
